/**
 * @fileoverview Authentication handling for Sandbox Platform SDK
 */

import { AuthConfig } from './types'
import { AuthenticationError } from './errors'

/**
 * Environment variable names for API configuration
 */
const ENV_VARS = {
  API_KEY: 'SBX_API_KEY',
  JWT_TOKEN: 'SBX_JWT_TOKEN',
  BASE_URL: 'SBX_BASE_URL'
} as const

/**
 * Default API base URL
 */
const DEFAULT_BASE_URL = 'https://api.apps.sandbox.9hz.dev'

/**
 * Authentication manager class
 */
export class AuthManager {
  private apiKey?: string
  private jwtToken?: string
  private baseUrl: string

  constructor(config?: AuthConfig) {
    // Initialize from config or environment variables
    this.apiKey = config?.apiKey || this.getEnvVar(ENV_VARS.API_KEY)
    this.jwtToken = config?.jwtToken || this.getEnvVar(ENV_VARS.JWT_TOKEN)
    this.baseUrl = config?.baseUrl || this.getEnvVar(ENV_VARS.BASE_URL) || DEFAULT_BASE_URL

    // Remove trailing slash from base URL
    this.baseUrl = this.baseUrl.replace(/\/$/, '')

    // Validate that we have some form of authentication
    if (!this.apiKey && !this.jwtToken) {
      throw AuthenticationError.missingApiKey()
    }
  }

  /**
   * Get environment variable value (works in both Node.js and browser environments)
   */
  private getEnvVar(name: string): string | undefined {
    // Node.js environment
    if (typeof process !== 'undefined' && process.env) {
      return process.env[name]
    }

    // Browser environment with Vite/Next.js
    try {
      if (typeof window !== 'undefined') {
        // Try to access import.meta.env if available
        const metaEnv = (globalThis as any)?.importMeta?.env
        if (metaEnv) {
          return metaEnv[`VITE_${name}`] || metaEnv[`NEXT_PUBLIC_${name}`] || metaEnv[`REACT_APP_${name}`]
        }
      }
    } catch (e) {
      // Ignore errors accessing import.meta
    }

    return undefined
  }

  /**
   * Get the authorization header value
   */
  getAuthorizationHeader(): string {
    if (this.apiKey) {
      return `Bearer ${this.apiKey}`
    }

    if (this.jwtToken) {
      return `Bearer ${this.jwtToken}`
    }

    throw AuthenticationError.missingApiKey()
  }

  /**
   * Get the base URL for API requests
   */
  getBaseUrl(): string {
    return this.baseUrl
  }

  /**
   * Get the API key (if available)
   */
  getApiKey(): string | undefined {
    return this.apiKey
  }

  /**
   * Get the JWT token (if available)
   */
  getJwtToken(): string | undefined {
    return this.jwtToken
  }

  /**
   * Check if API key authentication is being used
   */
  isUsingApiKey(): boolean {
    return !!this.apiKey
  }

  /**
   * Check if JWT authentication is being used
   */
  isUsingJwtToken(): boolean {
    return !!this.jwtToken
  }

  /**
   * Update the API key
   */
  setApiKey(apiKey: string): void {
    this.apiKey = apiKey
    this.jwtToken = undefined // Clear JWT token when setting API key
  }

  /**
   * Update the JWT token
   */
  setJwtToken(jwtToken: string): void {
    this.jwtToken = jwtToken
    this.apiKey = undefined // Clear API key when setting JWT token
  }

  /**
   * Update the base URL
   */
  setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl.replace(/\/$/, '')
  }

  /**
   * Clear authentication credentials
   */
  clearAuth(): void {
    this.apiKey = undefined
    this.jwtToken = undefined
  }

  /**
   * Validate the current authentication configuration
   */
  validateAuth(): void {
    if (!this.apiKey && !this.jwtToken) {
      throw AuthenticationError.missingApiKey()
    }

    // Validate API key format (should start with 'sbp_')
    if (this.apiKey && !this.apiKey.startsWith('sbp_')) {
      throw new AuthenticationError(
        'API key should start with "sbp_"',
        'INVALID_API_KEY_FORMAT'
      )
    }

    // Basic JWT format validation (should have 3 parts separated by dots)
    if (this.jwtToken && this.jwtToken.split('.').length !== 3) {
      throw new AuthenticationError(
        'JWT token format is invalid',
        'INVALID_JWT_FORMAT'
      )
    }
  }

  /**
   * Create request headers with authentication
   */
  createHeaders(additionalHeaders: Record<string, string> = {}): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'User-Agent': '@teamnhz/sandbox-sdk/1.1.0',
      'Authorization': this.getAuthorizationHeader(),
      ...additionalHeaders
    }

    return headers
  }
}

/**
 * Utility function to create AuthManager instance
 */
export function createAuthManager(config?: AuthConfig): AuthManager {
  return new AuthManager(config)
}

/**
 * Utility function to check if a string looks like an API key
 */
export function isApiKey(token: string): boolean {
  return token.startsWith('sbp_')
}

/**
 * Utility function to check if a string looks like a JWT token
 */
export function isJwtToken(token: string): boolean {
  return token.split('.').length === 3
}

/**
 * Auto-detect authentication type from token string
 */
export function detectAuthType(token: string): 'api-key' | 'jwt' | 'unknown' {
  if (isApiKey(token)) {
    return 'api-key'
  }

  if (isJwtToken(token)) {
    return 'jwt'
  }

  return 'unknown'
}